/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { useContext, useState, useCallback, useMemo, useEffect } from 'react';

import * as AppPropTypes from '../lib/PropTypes';

import { useApi } from './ApiContext';
import { useUser } from './AuthContext';

const FavoritesContext = React.createContext(null);

const useFavoritesContext = () => useContext(FavoritesContext);

export const useFavoriteItems = () => {
    const { favorites } = useFavoritesContext();
    return favorites;
};

export const useSetFavorites = () => {
    const { setFavorites } = useFavoritesContext();
    return setFavorites;
};

export const useFavoritesByType = (type) => {
    const favorites = useFavoriteItems();
    return (favorites || []).filter((it) => it.type === type);
};

export const useHasFavorite = () => {
    const favorites = useFavoriteItems();
    return useCallback(
        (type, id) =>
            (favorites || []).filter((it) => it.type === type && it.item.id === id).length > 0,
        [favorites],
    );
};

export const useAddFavorite = () => {
    const setFavorites = useSetFavorites();
    const favorites = useFavoriteItems();
    const api = useApi();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const addFavorite = useCallback(
        (type = null, id = null) => {
            if (type !== null && id !== null) {
                setLoading(true);
                api.favorites
                    .createOrUpdate({ type, id })
                    .then((newFavorite) => {
                        const { id: newId } = newFavorite || {};
                        setFavorites([...favorites.filter((it) => it.id !== newId), newFavorite]);
                        setLoading(false);
                    })
                    .catch(() => {
                        setError(true);
                        setLoading(false);
                    });
            }
        },
        [favorites, api, setFavorites, setLoading, setError],
    );

    return {
        error,
        loading,
        addFavorite,
    };
};

export const useRemoveFavorite = () => {
    const setFavorites = useSetFavorites();
    const favorites = useFavoriteItems();
    const api = useApi();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const removeFavorite = useCallback(
        (type = null, id = null) => {
            if (type !== null && id !== null) {
                setLoading(true);
                api.favorites
                    .delete({ type, id })
                    .then(() => {
                        setFavorites([
                            ...favorites.filter(
                                (it) =>
                                    it.type !== type ||
                                    parseInt(it.item.id, 10) !== parseInt(id, 10),
                            ),
                        ]);
                        setLoading(false);
                    })
                    .catch(() => {
                        setError(true);
                        setLoading(false);
                    });
            }
        },
        [favorites, api, setFavorites, setLoading, setError],
    );

    return {
        error,
        loading,
        removeFavorite,
    };
};

export const useFavorites = () => {
    const favorites = useHasFavorite();
    const hasFavorite = useHasFavorite();
    const { addFavorite, loading: loadingAdd } = useAddFavorite();
    const { removeFavorite, loading: loadingRemove } = useRemoveFavorite();
    return {
        favorites,
        hasFavorite,
        addFavorite,
        removeFavorite,
        loading: loadingAdd || loadingRemove,
    };
};

const propTypes = {
    favorites: AppPropTypes.favorites,
    children: PropTypes.node.isRequired,
};

const defaultProps = {
    favorites: null,
};

export const FavoritesProvider = ({ favorites: initialFavorites, children }) => {
    const [favorites, setFavorites] = useState(initialFavorites);
    const api = useApi();
    const user = useUser();
    const { id = null } = user || {};

    useEffect(() => {
        if (api !== null && user !== null && initialFavorites === null) {
            api.favorites.get().then((response) => {
                setFavorites(response);
            });
        }
    }, [id, initialFavorites, api, setFavorites]);

    useEffect(() => {
        if (user === null) {
            setFavorites(null);
        }
    }, [user, setFavorites]);

    const value = useMemo(() => ({ favorites, setFavorites }), [favorites, setFavorites]);

    return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

FavoritesProvider.propTypes = propTypes;
FavoritesProvider.defaultProps = defaultProps;

export default FavoritesContext;
